import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Pencil } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/components/ui/use-toast"

interface RenameWorkspaceDialogProps {
  workspaceId: string
  currentName: string
  onRename: () => void
}

export function RenameWorkspaceDialog({ workspaceId, currentName, onRename }: RenameWorkspaceDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [workspaceName, setWorkspaceName] = useState(currentName)
  const [isLoading, setIsLoading] = useState(false)

  const handleRenameWorkspace = async () => {
    if (!workspaceName.trim()) return

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({ name: workspaceName })
        .eq("id", workspaceId)

      if (error) throw error

      toast({
        title: t("common.workspaces.renamed"),
        description: t("common.workspaces.renamedDescription"),
      })
      setOpen(false)
      onRename()
    } catch (error) {
      console.error("Error renaming workspace:", error)
      toast({
        title: t("common.error"),
        description: t("common.workspaces.errorRenaming"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
          <span className="sr-only">{t("common.workspaces.rename")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.workspaces.rename")}</DialogTitle>
          <DialogDescription>
            {t("common.workspaces.renameDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("common.workspaces.name")}</Label>
            <Input
              id="name"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder={t("common.workspaces.namePlaceholder")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={handleRenameWorkspace}
            disabled={isLoading}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}